/**
 * BatchContext — lightweight global store for the currently selected batch.
 *
 * The TopNav BatchSelector sets `selectedBatchId`; Dashboard and other
 * top-level pages read it. Batch-detail pages still get batchId via
 * `useParams()` (they are scoped to `/batches/:batchId/...`).
 *
 * Persisted to localStorage under `efs.selected_batch` so the selection
 * survives a page refresh.
 */

import { createContext, useContext, useState, type ReactNode } from 'react';

const STORAGE_KEY = 'efs.selected_batch';

interface BatchContextValue {
  batchId: string | null;
  setBatchId: (id: string | null) => void;
}

const BatchContext = createContext<BatchContextValue>({
  batchId: null,
  setBatchId: () => undefined,
});

const safeLocalStorage = {
  getItem: (key: string): string | null => {
    try {
      return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
    } catch {
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem(key, value);
    } catch {
      // ignore
    }
  },
  removeItem: (key: string): void => {
    try {
      if (typeof localStorage !== 'undefined') localStorage.removeItem(key);
    } catch {
      // ignore
    }
  },
};

export const BatchProvider = ({ children }: { children: ReactNode }) => {
  const [batchId, setLocalBatchId] = useState<string | null>(() =>
    safeLocalStorage.getItem(STORAGE_KEY),
  );

  const setBatchId = (id: string | null) => {
    setLocalBatchId(id);
    if (id) {
      safeLocalStorage.setItem(STORAGE_KEY, id);
    } else {
      safeLocalStorage.removeItem(STORAGE_KEY);
    }
  };

  return <BatchContext.Provider value={{ batchId, setBatchId }}>{children}</BatchContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components -- hook colocated with its provider
export const useBatchContext = () => useContext(BatchContext);
